// Provider-agnostic chat() entry point. Routes to one of the supported
// providers using runtime config supplied by the caller (the AI orchestrator).

import { chatAnthropic } from './providers/anthropic';
import { chatOpenAI } from './providers/openai';
import { chatGemini } from './providers/gemini';
import { chatOpenline } from './providers/openline';
import type { ChatRequest, ChatResult, Provider } from './types';

export interface ProviderConfig {
  apiKey: string;
  baseUrl: string;
}

export interface ChatTransportConfig {
  /** Per-provider config. Caller supplies only the providers they intend to use. */
  anthropic?: ProviderConfig;
  openai?: ProviderConfig;
  gemini?: ProviderConfig;
  openline?: ProviderConfig;
  /** Any OpenAI-compatible endpoint — routed through the OpenAI wire format. */
  custom?: ProviderConfig;
}

export async function chat(
  request: ChatRequest,
  transport: ChatTransportConfig,
): Promise<ChatResult> {
  const provider: Provider = request.opts.provider;
  const config = transport[provider];
  if (!config) {
    throw new Error(
      `[ai/chat] Provider "${provider}" is not configured. Set the corresponding env vars and refresh.`,
    );
  }
  switch (provider) {
    case 'anthropic':
      return chatAnthropic(request, config);
    case 'openai':
      return chatOpenAI(request, config);
    case 'gemini':
      return chatGemini(request, config);
    case 'openline':
      return chatOpenline(request, config);
    case 'custom':
      // OpenAI-compatible endpoint — same wire format, caller-supplied baseUrl.
      return chatOpenAI(request, config);
  }
}
